import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  availabilityOf,
  buildParams,
  getBrandOptions,
  getClasses,
  getFamilies,
  getInventoryCounts,
  getOntology,
  withAvailability,
  type Availability,
  type BrandFamiliesResponse,
  type BrandOptionsResponse,
  type CarFilter,
  type ClassOption,
  type ClassesResponse,
  type FamilyEntry,
  type InventoryCountsResponse,
  type LookupOption,
  type OntologyNode,
  type TrimOption,
  type VariantOption,
} from "../api/client";
import { usePersistedState } from "../hooks/usePersistedState";

// Debounce window for the faceted counts/families refetch — dragging a
// price slider or typing a mileage bound fires an onChange per
// keystroke/pixel; this coalesces those into one request per settled
// filter state.
const FACET_DEBOUNCE_MS = 200;

// Per-brand options cache (models, packs, interiors, wheels) — fetched
// once per brand from /api/brands/{brand}/options. Survives component
// remounts within the SPA's lifetime so the sidebar stays snappy when
// the user toggles between pages of the same brand.
const _optionsCache = new Map<string, LookupOption[]>();
const _classesCache = new Map<string, ClassesResponse>();
// #29 — families + inventory-counts caches. Both depend on the
// availability posture (the count baseline shifts when the operator
// changes the Availability dropdown) — the key folds in the serialized
// filter, which carries `include_disappeared` / `only_disappeared`.
const _familiesCache = new Map<string, BrandFamiliesResponse>();
// bafd8fd — per-brand variants / trims caches from /api/brands/{brand}/options.
const _variantsCache = new Map<string, VariantOption[]>();
const _trimsCache = new Map<string, TrimOption[]>();
// Models and variants/trims come from the same /options response but are
// cached independently (so neither blocks the other), which meant the two
// effects below both missed their cache on a cold mount and issued the same
// request in parallel. Share the in-flight promise so /options is fetched
// once per brand; the entry is dropped on settle so a failure can be retried.
const _optionsInFlight = new Map<string, Promise<BrandOptionsResponse>>();

function fetchBrandOptionsOnce(
  brandCode: string,
): Promise<BrandOptionsResponse> {
  const pending = _optionsInFlight.get(brandCode);
  if (pending) return pending;
  const p = getBrandOptions(brandCode).finally(() => {
    _optionsInFlight.delete(brandCode);
  });
  _optionsInFlight.set(brandCode, p);
  return p;
}
// Per-axis car counts for the FilterSidebar bracket suffixes. Keyed
// by (brand, availability posture) — see the families cache note.
const _countsCache = new Map<string, InventoryCountsResponse>();

// Faceted: the cache key now folds in a stable serialization of the full
// active filter (via `buildParams`), not just brand + include_disappeared.
// Otherwise a cache hit from an unfiltered fetch would get served back
// while a filter is active, showing stale universe-wide counts.
function countsCacheKey(
  brandCode: string,
  includeDisappeared: boolean,
  filterParams: string,
): string {
  return `${brandCode}|${includeDisappeared ? "1" : "0"}|${filterParams}`;
}

/** True when an option's live count is EXPLICITLY 0 under the active filter
 *  AND it isn't currently selected. Selected zero-count options stay
 *  enabled so the operator can always untick their own selection.
 *
 *  `undefined` (the key absent from the counts map) is treated as *unknown*,
 *  not zero, and never greys the option out — this is the state every
 *  option is in for the brief window before the debounced counts fetch
 *  resolves (or if it fails entirely), and greying everything out during
 *  that window would block interaction that used to be instant. Only a
 *  map that has actually loaded and explicitly reports 0 greys an option.
 */
function isGreyedOut(count: number | undefined, selected: boolean): boolean {
  return count === 0 && !selected;
}

// Shared className fragments for a greyed-out (zero-count, unselected)
// option row and its label text.
const GREYED_LABEL = "opacity-50 cursor-not-allowed";
const GREYED_TEXT = "text-gray-300";
// Per-brand expand/collapse memory for the hierarchy tree. Survives
// FilterSidebar remounts (tab navigation) so opening Audi → Q4 e-tron
// → Q4 50 quattro and clicking "Depreciation" doesn't snap the tree
// back shut. Cleared on full reload; per brand because the carline
// keys are brand-scoped.
const _expandedFamiliesByBrand = new Map<string, Set<string>>();
const _collapsedModelsByBrand = new Map<string, Set<string>>();
const _expandedSlugsByBrand = new Map<string, Set<string>>();

export interface FilterSidebarProps {
  filter: CarFilter;
  onChange: (f: CarFilter) => void;
  brandCode: string;
}

type ChipState = "off" | "include" | "exclude";

interface OntologyTree {
  byId: Map<string, OntologyNode>;
  children: Map<string | null, OntologyNode[]>;
}

/** Build slug -> node and parent-slug -> children indexes from a flat node list. */
function buildTree(nodes: OntologyNode[]): OntologyTree {
  const byId = new Map<string, OntologyNode>();
  const children = new Map<string | null, OntologyNode[]>();
  for (const n of nodes) byId.set(n.node_id, n);
  for (const n of nodes) {
    const list = children.get(n.parent_node_id) ?? [];
    list.push(n);
    children.set(n.parent_node_id, list);
  }
  // Stable child ordering per the API's sort_order, then slug.
  for (const list of children.values()) {
    list.sort((a, b) => a.sort_order - b.sort_order || a.node_id.localeCompare(b.node_id));
  }
  return { byId, children };
}

/** All descendant slugs (excluding the seed itself). */
function descendants(tree: OntologyTree, slug: string): string[] {
  const out: string[] = [];
  const stack = [...(tree.children.get(slug) ?? [])];
  while (stack.length > 0) {
    const n = stack.pop()!;
    out.push(n.node_id);
    stack.push(...(tree.children.get(n.node_id) ?? []));
  }
  return out;
}

// Resize-handle bounds. The default 224px matches the original `w-56`;
// the min is wide enough to keep "Polestar 3" readable without
// truncation; the max is enough that long variant labels like
// "Standard Range Plus Rear-Wheel Drive" fit on one line.
// Ontology "Colour" group slugs surfaced as dedicated sidebar blocks and
// therefore stripped from the generic capability tree (see FilterSidebar).
const COLOUR_GROUP_SLUGS = new Set(["interior_colour", "exterior_colour"]);

const SIDEBAR_WIDTH_DEFAULT = 224;
const SIDEBAR_WIDTH_MIN = 200;
const SIDEBAR_WIDTH_MAX = 560;
const SIDEBAR_WIDTH_KEY = "polestar.sidebarWidth";

// Pre-hook builds stored the width as a bare "224" string; JSON.parse reads
// that as the number 224, so old stored values stay valid.
function sanitizeSidebarWidth(raw: unknown): number | undefined {
  if (typeof raw !== "number" || Number.isNaN(raw)) return undefined;
  return Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, raw));
}

export default function FilterSidebar({
  filter,
  onChange,
  brandCode,
}: FilterSidebarProps) {
  // User-adjustable sidebar width, persisted across reloads. The drag
  // handle on the right edge updates this; the body listeners only
  // attach while the user is actively dragging.
  const [width, setWidth] = usePersistedState<number>(
    SIDEBAR_WIDTH_KEY,
    SIDEBAR_WIDTH_DEFAULT,
    sanitizeSidebarWidth,
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      const onMove = (ev: MouseEvent) => {
        const next = Math.max(
          SIDEBAR_WIDTH_MIN,
          Math.min(SIDEBAR_WIDTH_MAX, startWidth + (ev.clientX - startX)),
        );
        setWidth(next);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      // Lock the page cursor + suppress text selection while dragging
      // so the handle stays responsive even when the pointer crosses
      // labels and checkboxes inside the sidebar.
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width],
  );

  const [models, setModels] = useState<LookupOption[]>(
    () => _optionsCache.get(brandCode) ?? [],
  );

  // Fetch the brand's models from /api/brands/{brand}/options. Cached
  // per brand so subsequent renders are instant. The fetch is fire-and-
  // forget; if it fails, the chip block stays empty (matches the
  // pre-fetch hardcoded behaviour for unknown brands).
  useEffect(() => {
    if (_optionsCache.has(brandCode)) {
      setModels(_optionsCache.get(brandCode) ?? []);
      return;
    }
    let cancelled = false;
    fetchBrandOptionsOnce(brandCode)
      .then((opts) => {
        _optionsCache.set(brandCode, opts.models);
        if (!cancelled) setModels(opts.models);
      })
      .catch(() => {
        // Network/404 — silently degrade to empty model list.
      });
    return () => {
      cancelled = true;
    };
  }, [brandCode]);

  // M11.B — class taxonomy options for the active brand. Fetched from
  // /api/classes; cached per brand. When the brand changes, any selected
  // class codes that aren't represented in the new brand's options are
  // dropped from the filter (per docs/feature-class-filter.md §6).
  const [classes, setClasses] = useState<ClassesResponse>(
    () => _classesCache.get(brandCode) ?? { body_classes: [], size_classes: [] },
  );

  useEffect(() => {
    const cached = _classesCache.get(brandCode);
    if (cached) {
      setClasses(cached);
      pruneInvalidClassChips(cached);
      return;
    }
    let cancelled = false;
    getClasses(brandCode)
      .then((c) => {
        _classesCache.set(brandCode, c);
        if (!cancelled) {
          setClasses(c);
          pruneInvalidClassChips(c);
        }
      })
      .catch(() => {
        // Network/404 — degrade silently. Selections survive in the URL/state
        // but no chip block renders.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandCode]);

  // The bracketed sidebar counts mirror the inventory list's
  // live-vs-disappeared posture, and — faceted — now also react to every
  // other active filter axis. The server returns exclude-self counts
  // (an axis's own selection doesn't zero the whole axis out) across the
  // brand-wide universe of values, including zero-count entries, which
  // the sidebar renders greyed rather than hidden.
  const includeDisappeared = filter.include_disappeared ?? true;
  // Stable string serialization of the full active filter — used as the
  // cache key and to detect "did the filter actually change" without
  // relying on object identity (see the debounced effects below).
  const filterParams = buildParams(filter);

  // #29 — model-family hierarchy. Replaces the flat Models block:
  // each family renders as a row with a chevron; expanding shows its
  // member carlines (which still write to the existing `models`
  // filter). Selecting a family writes to `family_codes`. The synthetic
  // `_unmapped` family from the API renders with a faded operator hint.
  const [families, setFamilies] = useState<FamilyEntry[]>(
    () =>
      _familiesCache.get(
        countsCacheKey(brandCode, includeDisappeared, filterParams),
      )?.families ?? [],
  );
  // Per-axis car counts, rendered as bracketed suffixes next to option
  // labels. Defaults to empty so the sidebar still renders if the
  // endpoint is unreachable — labels just drop the `(N)` suffix.
  const [counts, setCounts] = useState<InventoryCountsResponse | null>(
    () =>
      _countsCache.get(
        countsCacheKey(brandCode, includeDisappeared, filterParams),
      ) ?? null,
  );
  const countsDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const key = countsCacheKey(brandCode, includeDisappeared, filterParams);
    const cached = _countsCache.get(key);
    if (cached) {
      setCounts(cached);
      return;
    }
    let cancelled = false;
    clearTimeout(countsDebounceRef.current);
    countsDebounceRef.current = setTimeout(() => {
      getInventoryCounts(brandCode, { includeDisappeared, filter })
        .then((resp) => {
          _countsCache.set(key, resp);
          if (!cancelled) setCounts(resp);
        })
        .catch(() => {
          // Silent degrade — sidebar renders without the bracketed counts.
        });
    }, FACET_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(countsDebounceRef.current);
    };
    // filterParams is the content-equality proxy for `filter`; both are
    // listed so the effect has the actual object to pass to the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandCode, includeDisappeared, filterParams]);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(
    () => _expandedFamiliesByBrand.get(brandCode) ?? new Set(),
  );
  // Per-model collapse state for the inline Variants + Trims rows.
  // Models are *default-expanded*; the Set tracks model_codes the
  // operator has explicitly collapsed (mirrors the admin UI's posture).
  const [collapsedModels, setCollapsedModels] = useState<Set<string>>(
    () => _collapsedModelsByBrand.get(brandCode) ?? new Set(),
  );
  // Mirror state changes back to the module-level caches so the tree
  // shape survives tab navigation (each page remounts FilterSidebar).
  useEffect(() => {
    _expandedFamiliesByBrand.set(brandCode, expandedFamilies);
  }, [brandCode, expandedFamilies]);
  useEffect(() => {
    _collapsedModelsByBrand.set(brandCode, collapsedModels);
  }, [brandCode, collapsedModels]);

  const familiesDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const key = countsCacheKey(brandCode, includeDisappeared, filterParams);
    const cached = _familiesCache.get(key);
    if (cached) {
      setFamilies(cached.families);
      return;
    }
    let cancelled = false;
    clearTimeout(familiesDebounceRef.current);
    familiesDebounceRef.current = setTimeout(() => {
      getFamilies(brandCode, { includeDisappeared, filter })
        .then((resp) => {
          _familiesCache.set(key, resp);
          if (!cancelled) setFamilies(resp.families);
        })
        .catch(() => {
          // Silent degrade — sidebar falls back to flat Models block when
          // present, otherwise renders no model picker at all.
        });
    }, FACET_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(familiesDebounceRef.current);
    };
    // filterParams is the content-equality proxy for `filter`; both are
    // listed so the effect has the actual object to pass to the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandCode, includeDisappeared, filterParams]);

  function toggleFamilyExpanded(familyCode: string) {
    setExpandedFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(familyCode)) next.delete(familyCode);
      else next.add(familyCode);
      return next;
    });
  }

  function toggleModelCollapsed(modelCode: string) {
    setCollapsedModels((prev) => {
      const next = new Set(prev);
      if (next.has(modelCode)) next.delete(modelCode);
      else next.add(modelCode);
      return next;
    });
  }

  function toggleFamily(familyCode: string) {
    const cur = filter.family_codes ?? [];
    const next = cur.includes(familyCode)
      ? cur.filter((f) => f !== familyCode)
      : [...cur, familyCode];
    onChange({ ...filter, family_codes: next.length ? next : undefined });
  }

  // bafd8fd — Variant and Trim state. Both start collapsed (per spec: deeper
  // level than Family, so the user drills in explicitly).
  const [variants, setVariants] = useState<VariantOption[]>(
    () => _variantsCache.get(brandCode) ?? [],
  );
  const [trims, setTrims] = useState<TrimOption[]>(
    () => _trimsCache.get(brandCode) ?? [],
  );
  // Variants + Trims now render inline inside each Model row in the
  // family tree (see the carlines render below). The standalone-
  // section expansion state has been removed.

  useEffect(() => {
    // Both variants and trims come from the same /options endpoint. We fetch
    // models separately in the effect above; here we fetch variants + trims
    // and cache them independently so neither blocks the other.
    if (_variantsCache.has(brandCode) && _trimsCache.has(brandCode)) {
      setVariants(_variantsCache.get(brandCode) ?? []);
      setTrims(_trimsCache.get(brandCode) ?? []);
      return;
    }
    let cancelled = false;
    fetchBrandOptionsOnce(brandCode)
      .then((opts) => {
        _variantsCache.set(brandCode, opts.variants);
        _trimsCache.set(brandCode, opts.trims);
        if (!cancelled) {
          setVariants(opts.variants);
          setTrims(opts.trims);
        }
      })
      .catch(() => {
        // Network/404 — degrade silently to empty lists.
      });
    return () => {
      cancelled = true;
    };
  }, [brandCode]);

  // Toggle values are the composite "MODEL:code" string (spec §2.4.2),
  // not the bare code — otherwise selecting e.g. "MY Long Range"
  // would also select "M3 Long Range" because both share variant_code
  // `lr_awd`.
  function toggleVariant(composite: string) {
    const cur = filter.variant_codes ?? [];
    const next = cur.includes(composite)
      ? cur.filter((c) => c !== composite)
      : [...cur, composite];
    onChange({ ...filter, variant_codes: next.length ? next : undefined });
  }

  function toggleTrim(composite: string) {
    const cur = filter.trim_codes ?? [];
    const next = cur.includes(composite)
      ? cur.filter((c) => c !== composite)
      : [...cur, composite];
    onChange({ ...filter, trim_codes: next.length ? next : undefined });
  }

  function pruneInvalidClassChips(c: ClassesResponse) {
    const validBody = new Set(c.body_classes.map((o) => o.code));
    const validSize = new Set(c.size_classes.map((o) => o.code));
    const curBody = filter.body_classes ?? [];
    const curSize = filter.size_classes ?? [];
    const nextBody = curBody.filter((b) => validBody.has(b));
    const nextSize = curSize.filter((s) => validSize.has(s));
    if (nextBody.length === curBody.length && nextSize.length === curSize.length) {
      return;
    }
    onChange({
      ...filter,
      body_classes: nextBody.length ? nextBody : undefined,
      size_classes: nextSize.length ? nextSize : undefined,
    });
  }

  function toggleClass(axis: "body_classes" | "size_classes", code: string) {
    const cur = filter[axis] ?? [];
    const next = cur.includes(code)
      ? cur.filter((c) => c !== code)
      : [...cur, code];
    onChange({ ...filter, [axis]: next.length ? next : undefined });
  }

  function toggleDrivetrain(code: string) {
    const cur = filter.drivetrains ?? [];
    const next = cur.includes(code)
      ? cur.filter((c) => c !== code)
      : [...cur, code];
    onChange({ ...filter, drivetrains: next.length ? next : undefined });
  }

  function toggleModelYear(year: number) {
    const cur = filter.model_years ?? [];
    const next = cur.includes(year)
      ? cur.filter((y) => y !== year)
      : [...cur, year];
    onChange({ ...filter, model_years: next.length ? next : undefined });
  }

  function toggleBodyType(code: string) {
    const cur = filter.body_types ?? [];
    const next = cur.includes(code)
      ? cur.filter((c) => c !== code)
      : [...cur, code];
    onChange({ ...filter, body_types: next.length ? next : undefined });
  }

  function toggleBattery(value: number) {
    const cur = filter.battery_kwh_values ?? [];
    const next = cur.includes(value)
      ? cur.filter((c) => c !== value)
      : [...cur, value];
    onChange({ ...filter, battery_kwh_values: next.length ? next : undefined });
  }

  function toggleInteriorMaterial(code: string) {
    const cur = filter.interior_materials ?? [];
    const next = cur.includes(code)
      ? cur.filter((c) => c !== code)
      : [...cur, code];
    onChange({ ...filter, interior_materials: next.length ? next : undefined });
  }

  // Simple include-only toggle for the dedicated colour blocks. Writes the
  // capability slug into `capabilities_any_of` (and clears it from
  // `capabilities_excluded`), matching the ontology chips' wire format.
  function toggleColour(slug: string) {
    const include = new Set(filter.capabilities_any_of ?? []);
    const exclude = new Set(filter.capabilities_excluded ?? []);
    if (include.has(slug)) {
      include.delete(slug);
    } else {
      include.add(slug);
      exclude.delete(slug);
    }
    onChange({
      ...filter,
      capabilities_any_of: include.size ? [...include] : undefined,
      capabilities_excluded: exclude.size ? [...exclude] : undefined,
    });
  }

  const [ontologyNodes, setOntologyNodes] = useState<OntologyNode[]>([]);

  // Interior + exterior colour are surfaced as dedicated, clearly-labelled
  // blocks (below), so strip those two "Colour" groups out of the generic
  // ontology tree — otherwise the sidebar shows two ambiguous "Colour"
  // sub-sections (one under Interior, one under Exterior) that read as the
  // same thing.
  const tree = useMemo(
    () =>
      buildTree(
        ontologyNodes.filter(
          (n) =>
            !COLOUR_GROUP_SLUGS.has(n.node_id) &&
            (n.parent_node_id === null ||
              !COLOUR_GROUP_SLUGS.has(n.parent_node_id)),
        ),
      ),
    [ontologyNodes],
  );

  // Top-level sections (parent_node_id === null), in sort_order.
  const topLevel = useMemo(
    () => tree.children.get(null) ?? [],
    [tree],
  );

  // Colour-leaf nodes active for the brand, read straight from the raw node
  // list (the colour groups are excluded from `tree` above). Each is a
  // shortcut writing to the same `capabilities_any_of` filter the ontology
  // chips use — the server expands the slug to the brand's paint/interior codes.
  const interiorColours = useMemo(
    () =>
      ontologyNodes.filter(
        (n) =>
          n.parent_node_id === "interior_colour" &&
          n.active_brand_codes.includes(brandCode),
      ),
    [ontologyNodes, brandCode],
  );
  const exteriorColours = useMemo(
    () =>
      ontologyNodes.filter(
        (n) =>
          n.parent_node_id === "exterior_colour" &&
          n.active_brand_codes.includes(brandCode),
      ),
    [ontologyNodes, brandCode],
  );

  // Per-parent expanded set: when a parent's slug is here, its leaf
  // children render. Hierarchy (non-leaf) children always render.
  // Default: empty — every leaf is collapsed under its parent until the
  // user expands it.
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(
    () => _expandedSlugsByBrand.get(brandCode) ?? new Set(),
  );
  useEffect(() => {
    _expandedSlugsByBrand.set(brandCode, expandedSlugs);
  }, [brandCode, expandedSlugs]);

  function toggleExpanded(slug: string) {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    // Pass the active brand so the response includes the unmapped bucket
    // pseudo-nodes for that brand (per docs/feature-capability-ontology.md §4.6).
    getOntology(brandCode)
      .then((o) => {
        if (!cancelled) {
          setOntologyNodes(o.nodes);
          // Auto-expand top-level sections so their leaf children (e.g.
          // Wheels → 19/20/21/22 inch) show immediately. The user can
          // still collapse via the caret. Deeper levels stay
          // collapsed-by-default. Merge with any existing expansions
          // so a brand re-fetch preserves the user's deeper choices.
          setExpandedSlugs((prev) => {
            const next = new Set(prev);
            for (const n of o.nodes) {
              if (n.parent_node_id === null) next.add(n.node_id);
            }
            return next;
          });
        }
      })
      .catch(() => {
        // Non-fatal; sidebar degrades gracefully if the ontology fetch fails.
      });
    return () => {
      cancelled = true;
    };
  }, [brandCode]);

  function toggleModel(code: string) {
    const current = filter.models ?? [];
    const next = current.includes(code)
      ? current.filter((m) => m !== code)
      : [...current, code];
    onChange({ ...filter, models: next.length ? next : undefined });
  }

  function setNum(key: keyof CarFilter, raw: string) {
    const val = raw === "" ? undefined : Number(raw);
    onChange({ ...filter, [key]: val });
  }

  /** Returns the chip's current state by walking *only* the visible filter. */
  function chipState(slug: string): ChipState {
    if ((filter.capabilities_excluded ?? []).includes(slug)) return "exclude";
    if ((filter.capabilities_any_of ?? []).includes(slug)) return "include";
    return "off";
  }

  /** Three-state cycle: off → include → exclude → off.
   *  Wire shape locked per docs/feature-capability-ontology.md §13:
   *  client sends only the slug the user clicked. The server expander
   *  walks the tree and unions descendant raw codes. Descendant chips
   *  reflect the parent's state visually via `effectiveChipState` below
   *  but are not written into the filter unless the user clicks them.
   */
  function cycle(slug: string) {
    const state = chipState(slug);
    const include = new Set(filter.capabilities_any_of ?? []);
    const exclude = new Set(filter.capabilities_excluded ?? []);
    include.delete(slug);
    exclude.delete(slug);
    if (state === "off") include.add(slug);
    else if (state === "include") exclude.add(slug);
    // state === "exclude" → leave both unset (off)
    onChange({
      ...filter,
      capabilities_any_of: include.size ? [...include] : undefined,
      capabilities_excluded: exclude.size ? [...exclude] : undefined,
    });
  }

  /** A chip's *effective* state — its own state, OR the nearest selected
   *  ancestor's state (for visual propagation per §4.6). When a parent is
   *  selected, every descendant renders with the parent's state but their
   *  slugs are not written to the filter (server-side expansion handles it).
   *  When *some* descendants override the parent, the parent renders
   *  indeterminate — see `parentVisualState`.
   */
  function effectiveChipState(slug: string): ChipState {
    const own = chipState(slug);
    if (own !== "off") return own;
    // Walk up the tree; nearest selected ancestor wins.
    let cur = tree.byId.get(slug)?.parent_node_id ?? null;
    while (cur) {
      const s = chipState(cur);
      if (s !== "off") return s;
      cur = tree.byId.get(cur)?.parent_node_id ?? null;
    }
    return "off";
  }

  /** A non-leaf renders "indeterminate" when its descendants have a *mix*
   *  of states (some on, some off). Used for the parent chip glyph.
   */
  function parentVisualState(slug: string): ChipState | "indeterminate" {
    const own = chipState(slug);
    if (own !== "off") return own;
    const desc = descendants(tree, slug);
    if (desc.length === 0) return "off";
    const states = new Set<ChipState>();
    for (const d of desc) states.add(chipState(d));
    states.delete("off");
    if (states.size === 0) return "off";
    if (states.size === 1) return [...states][0]!;
    return "indeterminate";
  }

  return (
    <aside
      // The sidebar always sits alongside the listing. On phones (< md)
      // it takes a narrow fixed slice so the table gets the rest of the
      // width (the table scrolls horizontally for any overflow); from md
      // up it uses the drag-to-resize pixel width.
      style={{ "--sidebar-width": `${width}px` } as React.CSSProperties}
      className="relative shrink-0 w-40 md:w-[var(--sidebar-width)] bg-gray-50 border-r p-4 space-y-5"
    >
      {/* Drag handle on the right edge. Two-pixel-wide hit area with a
          slightly larger hover/active zone to make it forgiving — same
          interaction pattern most IDE side panels use. Hidden on phones
          where the sidebar is full-width and there's nothing to resize. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize filter sidebar"
        onMouseDown={handleResizeStart}
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400 hidden md:block"
        data-testid="sidebar-resize-handle"
      />
      <h2 className="font-semibold text-gray-700">
        Filter{" "}
        {counts && (
          <span className="font-normal text-gray-400 text-sm">
            ({counts.total})
          </span>
        )}
      </h2>

      {families.length > 0 ? (
        // #29 — Families section. Replaces the flat Models block; each
        // family is collapsible into its member carlines. Family rows
        // write to `family_codes`; carline rows still write to the
        // existing `models` filter so saved-filter shapes aren't broken.
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Model
          </p>
          {families.map((fam) => {
            const expanded = expandedFamilies.has(fam.family_code);
            const isUnmapped = fam.family_code === "_unmapped";
            const familyChecked = (filter.family_codes ?? []).includes(
              fam.family_code,
            );
            const familyGreyed = isGreyedOut(fam.count, familyChecked);
            return (
              <div key={fam.family_code} className="mb-0.5">
                <div
                  className={`flex items-center gap-1 text-sm py-0.5 ${
                    isUnmapped ? "italic text-gray-500" : ""
                  }`}
                >
                  <label
                    className={`flex items-center gap-2 flex-1 min-w-0 ${
                      familyGreyed ? GREYED_LABEL : "cursor-pointer"
                    }`}
                    aria-disabled={familyGreyed}
                  >
                    <input
                      type="checkbox"
                      checked={familyChecked}
                      onChange={() => !familyGreyed && toggleFamily(fam.family_code)}
                      disabled={familyGreyed}
                      aria-disabled={familyGreyed}
                      className="accent-blue-600"
                      title={
                        isUnmapped
                          ? "Carlines that no curated family claims yet — add them to seed/model_families.yml"
                          : undefined
                      }
                    />
                    <span className={`truncate ${familyGreyed ? GREYED_TEXT : ""}`}>
                      {fam.display_name}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({fam.count})
                    </span>
                  </label>
                  {fam.carlines.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleFamilyExpanded(fam.family_code)}
                      title={
                        expanded
                          ? "Hide carlines"
                          : `Show ${fam.carlines.length} carline${fam.carlines.length === 1 ? "" : "s"}`
                      }
                      aria-label={expanded ? "Collapse" : "Expand"}
                      aria-expanded={expanded}
                      className="text-xs text-gray-500 hover:text-gray-700 px-1 cursor-pointer"
                    >
                      <span className="select-none">
                        {expanded ? "▾" : "▸"}
                      </span>
                    </button>
                  )}
                </div>
                {expanded && fam.carlines.length > 0 && (
                  <div className="ml-5">
                    {fam.carlines.map(
                      ({ model_code, display_name, body_style, count }) => {
                        const modelVariants = variants.filter(
                          (v) => v.model_code === model_code,
                        );
                        const modelTrims = trims.filter(
                          (t) => t.model_code === model_code,
                        );
                        const hasSubAxes =
                          modelVariants.length > 0 || modelTrims.length > 0;
                        const modelExpanded = !collapsedModels.has(model_code);
                        const modelChecked = (filter.models ?? []).includes(
                          model_code,
                        );
                        const modelGreyed = isGreyedOut(count, modelChecked);
                        return (
                          <div key={model_code} className="mb-0.5">
                            <div className="flex items-center gap-1 text-xs py-0.5 text-gray-600">
                              <label
                                className={`flex items-center gap-2 flex-1 min-w-0 ${
                                  modelGreyed ? GREYED_LABEL : "cursor-pointer"
                                }`}
                                aria-disabled={modelGreyed}
                              >
                                <input
                                  type="checkbox"
                                  checked={modelChecked}
                                  onChange={() => !modelGreyed && toggleModel(model_code)}
                                  disabled={modelGreyed}
                                  aria-disabled={modelGreyed}
                                  className="accent-blue-600"
                                />
                                <span
                                  className={`truncate ${modelGreyed ? GREYED_TEXT : ""}`}
                                >
                                  {display_name}
                                </span>
                                {/* #31 — per-family body_style label. */}
                                {body_style && (
                                  <span
                                    className="text-[10px] uppercase tracking-wide text-gray-400"
                                    data-testid={`body-style-${model_code}`}
                                  >
                                    · {body_style}
                                  </span>
                                )}
                                <span className="text-[10px] text-gray-400">
                                  ({count ?? 0})
                                </span>
                              </label>
                              {hasSubAxes && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleModelCollapsed(model_code)
                                  }
                                  title={
                                    modelExpanded
                                      ? "Hide variants/trims"
                                      : `Show ${modelVariants.length} variant${modelVariants.length === 1 ? "" : "s"}` +
                                        (modelTrims.length > 0
                                          ? ` + ${modelTrims.length} trim${modelTrims.length === 1 ? "" : "s"}`
                                          : "")
                                  }
                                  aria-label={
                                    modelExpanded ? "Collapse" : "Expand"
                                  }
                                  aria-expanded={modelExpanded}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-1 cursor-pointer"
                                >
                                  <span className="select-none">
                                    {modelExpanded ? "▾" : "▸"}
                                  </span>
                                </button>
                              )}
                            </div>
                            {hasSubAxes && modelExpanded && (
                              <div className="ml-5 mt-0.5">
                                {modelVariants.length > 0 && (
                                  <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-0.5">
                                      Variants
                                    </p>
                                    {modelVariants.map(
                                      ({ composite, label }) => {
                                        const variantChecked = (
                                          filter.variant_codes ?? []
                                        ).includes(composite);
                                        const variantGreyed = isGreyedOut(
                                          counts?.variants[composite],
                                          variantChecked,
                                        );
                                        return (
                                          <label
                                            key={composite}
                                            className={`flex items-center gap-2 text-[11px] py-0.5 ml-2 ${
                                              variantGreyed
                                                ? GREYED_LABEL
                                                : "cursor-pointer text-gray-600"
                                            }`}
                                            aria-disabled={variantGreyed}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={variantChecked}
                                              onChange={() =>
                                                !variantGreyed &&
                                                toggleVariant(composite)
                                              }
                                              disabled={variantGreyed}
                                              aria-disabled={variantGreyed}
                                              className="accent-blue-600"
                                            />
                                            <span
                                              className={`truncate ${variantGreyed ? GREYED_TEXT : ""}`}
                                            >
                                              {label}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                              ({counts?.variants[composite] ?? 0})
                                            </span>
                                          </label>
                                        );
                                      },
                                    )}
                                  </div>
                                )}
                                {modelTrims.length > 0 && (
                                  <div className="mt-1">
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-0.5">
                                      Trims
                                    </p>
                                    {modelTrims.map(
                                      ({ composite, label }) => {
                                        const trimChecked = (
                                          filter.trim_codes ?? []
                                        ).includes(composite);
                                        const trimGreyed = isGreyedOut(
                                          counts?.trims[composite],
                                          trimChecked,
                                        );
                                        return (
                                          <label
                                            key={composite}
                                            className={`flex items-center gap-2 text-[11px] py-0.5 ml-2 ${
                                              trimGreyed
                                                ? GREYED_LABEL
                                                : "cursor-pointer text-gray-600"
                                            }`}
                                            aria-disabled={trimGreyed}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={trimChecked}
                                              onChange={() =>
                                                !trimGreyed &&
                                                toggleTrim(composite)
                                              }
                                              disabled={trimGreyed}
                                              aria-disabled={trimGreyed}
                                              className="accent-blue-600"
                                            />
                                            <span
                                              className={`truncate ${trimGreyed ? GREYED_TEXT : ""}`}
                                            >
                                              {label}
                                            </span>
                                            <span className="text-[10px] text-gray-400">
                                              ({counts?.trims[composite] ?? 0})
                                            </span>
                                          </label>
                                        );
                                      },
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      ) : (
        // Fallback to the original flat Models list. Reached when the
        // families endpoint hasn't been wired (test harness without a
        // `getFamilies` mock) or when a brand has no `model_families`
        // rows yet (cold start before the seed migration runs).
        models.length > 0 && (
          <section>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              Model
            </p>
            {models.map(({ code, label }) => {
              const checked = (filter.models ?? []).includes(code);
              const greyed = isGreyedOut(counts?.models[code], checked);
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleModel(code)}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>
                    {label}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({counts?.models[code] ?? 0})
                  </span>
                </label>
              );
            })}
          </section>
        )
      )}

      {/* Variants and Trims now render INLINE inside the Family tree —
           each Model row carries a ▸/▾ toggle that surfaces its
           operator-curated variants + trims directly below it. The
           standalone sections that used to live here were removed
           because they duplicated the same data without the
           parent-child visual relationship the operator wanted. */}

      <section>
        <p className="text-xs font-medium text-gray-500 uppercase mb-1">
          Price (£)
        </p>
        <div className="flex gap-1">
          <input
            type="number"
            placeholder="Min"
            className="w-full border rounded px-2 py-1 text-sm"
            value={filter.price_min ?? ""}
            onChange={(e) => setNum("price_min", e.target.value)}
          />
          <input
            type="number"
            placeholder="Max"
            className="w-full border rounded px-2 py-1 text-sm"
            value={filter.price_max ?? ""}
            onChange={(e) => setNum("price_max", e.target.value)}
          />
        </div>
      </section>

      <section>
        <p className="text-xs font-medium text-gray-500 uppercase mb-1">
          Mileage
        </p>
        <div className="flex gap-1">
          <input
            type="number"
            placeholder="Min"
            className="w-full border rounded px-2 py-1 text-sm"
            value={filter.mileage_min ?? ""}
            onChange={(e) => setNum("mileage_min", e.target.value)}
          />
          <input
            type="number"
            placeholder="Max"
            className="w-full border rounded px-2 py-1 text-sm"
            value={filter.mileage_max ?? ""}
            onChange={(e) => setNum("mileage_max", e.target.value)}
          />
        </div>
      </section>

      {/* Model year filter — integer cars.model_year values, the fit's sole
           time axis (§4.1.4d). Option list and counts both come from the
           inventory-counts model_years map (no seeded lookup table), so the
           block only renders for brands whose inventory carries the field.
           Newest year first. */}
      {counts && Object.keys(counts.model_years ?? {}).length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Model year
          </p>
          {Object.keys(counts.model_years)
            .sort((a, b) => Number(b) - Number(a))
            .map((key) => {
              const year = Number(key);
              const checked = (filter.model_years ?? []).includes(year);
              const greyed = isGreyedOut(counts.model_years[key], checked);
              return (
                <label
                  key={key}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleModelYear(year)}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>
                    {key}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({counts.model_years[key]})
                  </span>
                </label>
              );
            })}
        </section>
      )}

      {/* Drivetrain filter — raw cars.drivetrain values (RWD / AWD / FWD).
           The option list and counts both come from the inventory-counts
           drivetrains map (there is no seeded lookup table), so the block
           only renders for brands whose live inventory carries the field. */}
      {counts && Object.keys(counts.drivetrains).length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Drivetrain
          </p>
          {Object.keys(counts.drivetrains)
            .sort()
            .map((code) => {
              const checked = (filter.drivetrains ?? []).includes(code);
              const greyed = isGreyedOut(counts.drivetrains[code], checked);
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleDrivetrain(code)}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>
                    {code}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({counts.drivetrains[code]})
                  </span>
                </label>
              );
            })}
        </section>
      )}

      {/* Body filter — per-car cars.body_type, folded to canonical labels
           (Saloon / Sport Turismo / Cross Turismo / SUV ...). This is NOT the
           CLASS > Body block below: that one is models.body_class, which is
           per-carline and carries a single value for a whole carline, so it
           cannot express the Taycan's three bodies. Option list and counts both
           come from the inventory-counts body_types map, so the block only
           renders for brands whose parser sets body_type. */}
      {counts && Object.keys(counts.body_types).length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Body
          </p>
          {Object.keys(counts.body_types)
            .sort()
            .map((code) => {
              const checked = (filter.body_types ?? []).includes(code);
              const greyed = isGreyedOut(counts.body_types[code], checked);
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleBodyType(code)}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>
                    {code}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({counts.body_types[code]})
                  </span>
                </label>
              );
            })}
        </section>
      )}

      {/* Battery filter — rounded whole-kWh cars.battery_kwh values.
           The option list and counts both come from the inventory-counts
           battery_kwh_values map (there is no seeded lookup table), so the
           block only renders for brands whose live inventory carries the
           field. */}
      {counts && Object.keys(counts.battery_kwh_values).length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Battery
          </p>
          {Object.keys(counts.battery_kwh_values)
            .sort((a, b) => Number(a) - Number(b))
            .map((code) => {
              const checked = (filter.battery_kwh_values ?? []).includes(
                Number(code),
              );
              const greyed = isGreyedOut(
                counts.battery_kwh_values[code],
                checked,
              );
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleBattery(Number(code))}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>{`${code} kWh`}</span>
                  <span className="text-xs text-gray-400">
                    ({counts.battery_kwh_values[code]})
                  </span>
                </label>
              );
            })}
        </section>
      )}

      {/* Interior filter — normalised cars.interior_material labels (e.g. the
           BMW iX "Atelier"/"Suite"/"Loft" worlds, Porsche's material vocabulary).
           Option list and counts both come from the inventory-counts
           interior_materials map (no seeded lookup), so the block only renders
           for brands whose live inventory carries the field. */}
      {counts && Object.keys(counts.interior_materials).length > 0 && (
        <section>
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">
            Interior
          </p>
          {Object.keys(counts.interior_materials)
            .sort((a, b) => a.localeCompare(b))
            .map((code) => {
              const checked = (filter.interior_materials ?? []).includes(code);
              const greyed = isGreyedOut(
                counts.interior_materials[code],
                checked,
              );
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 text-sm py-0.5 ${
                    greyed ? GREYED_LABEL : "cursor-pointer"
                  }`}
                  aria-disabled={greyed}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => !greyed && toggleInteriorMaterial(code)}
                    disabled={greyed}
                    aria-disabled={greyed}
                    className="accent-blue-600"
                  />
                  <span className={`truncate ${greyed ? GREYED_TEXT : ""}`}>{code}</span>
                  <span className="text-xs text-gray-400">
                    ({counts.interior_materials[code]})
                  </span>
                </label>
              );
            })}
        </section>
      )}

      {/* M11.B — class taxonomy filter. Two sibling sub-sections under
           a "Class" group; only renders for the brand's tagged options.
           Selections are brand-agnostic and survive a brand switch. */}
      {(classes.body_classes.length > 0 || classes.size_classes.length > 0) && (
        <section className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Class</p>
          {classes.body_classes.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-gray-500 mb-0.5">Body</p>
              {classes.body_classes.map((opt: ClassOption) => {
                const checked = (filter.body_classes ?? []).includes(opt.code);
                const greyed = isGreyedOut(opt.count, checked);
                return (
                  <label
                    key={`body-${opt.code}`}
                    className={`flex items-center gap-2 text-sm py-0.5 ${
                      greyed ? GREYED_LABEL : "cursor-pointer"
                    }`}
                    aria-disabled={greyed}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => !greyed && toggleClass("body_classes", opt.code)}
                      disabled={greyed}
                      aria-disabled={greyed}
                      className="accent-blue-600"
                    />
                    <span className={greyed ? GREYED_TEXT : ""}>{opt.label}</span>
                    <span className="text-xs text-gray-400">({opt.count})</span>
                  </label>
                );
              })}
            </div>
          )}
          {classes.size_classes.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-gray-500 mb-0.5">Size</p>
              {classes.size_classes.map((opt: ClassOption) => {
                const checked = (filter.size_classes ?? []).includes(opt.code);
                const greyed = isGreyedOut(opt.count, checked);
                return (
                  <label
                    key={`size-${opt.code}`}
                    className={`flex items-center gap-2 text-sm py-0.5 ${
                      greyed ? GREYED_LABEL : "cursor-pointer"
                    }`}
                    aria-disabled={greyed}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => !greyed && toggleClass("size_classes", opt.code)}
                      disabled={greyed}
                      aria-disabled={greyed}
                      className="accent-blue-600"
                    />
                    <span className={greyed ? GREYED_TEXT : ""}>{opt.label}</span>
                    <span className="text-xs text-gray-400">({opt.count})</span>
                  </label>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Dedicated, clearly-labelled colour blocks — flat, always-visible
           shortcuts to the Exterior → Colour and Interior → Colour leaves that
           otherwise sit collapsed (and ambiguously both labelled "Colour") in
           the ontology. Same `capabilities_any_of` wire the server expands. */}
      {[
        { label: "Exterior colour", colours: exteriorColours },
        { label: "Interior colour", colours: interiorColours },
      ].map(
        ({ label, colours }) =>
          colours.length > 0 && (
            <section key={label}>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                {label}
              </p>
              {colours.map((node) => (
                <label
                  key={node.node_id}
                  className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={(filter.capabilities_any_of ?? []).includes(
                      node.node_id,
                    )}
                    onChange={() => toggleColour(node.node_id)}
                    className="accent-blue-600"
                  />
                  <span>{node.label}</span>
                </label>
              ))}
            </section>
          ),
      )}

      {/* Ontology-driven capability sections (M9.A).
           Leaf chips collapse under their parent by default — click the
           caret on a parent to reveal the leaves. */}
      {topLevel.map((section) => (
        <OntologySection
          key={section.node_id}
          node={section}
          tree={tree}
          brandCode={brandCode}
          chipState={chipState}
          effectiveChipState={effectiveChipState}
          parentVisualState={parentVisualState}
          onCycle={cycle}
          expandedSlugs={expandedSlugs}
          onToggleExpanded={toggleExpanded}
        />
      ))}

      <section>
        <label
          className="block text-sm font-medium mb-1"
          htmlFor="availability-select"
        >
          Availability
        </label>
        <select
          id="availability-select"
          value={availabilityOf(filter)}
          onChange={(e) =>
            onChange(withAvailability(filter, e.target.value as Availability))
          }
          className="w-full border rounded px-2 py-1 text-sm"
        >
          <option value="recent">Available + unavailable (last 14 days)</option>
          <option value="available">Available now</option>
          <option value="unavailable">Unavailable (last 30 days)</option>
          <option value="unavailable_all_time">Unavailable (all time)</option>
          <option value="all">All (available + unavailable)</option>
        </select>
      </section>
    </aside>
  );
}

interface OntologySectionProps {
  node: OntologyNode;
  tree: OntologyTree;
  brandCode: string;
  chipState: (slug: string) => ChipState;
  effectiveChipState: (slug: string) => ChipState;
  parentVisualState: (slug: string) => ChipState | "indeterminate";
  onCycle: (slug: string) => void;
  expandedSlugs: Set<string>;
  onToggleExpanded: (slug: string) => void;
}

function OntologySection({
  node,
  tree,
  brandCode,
  chipState,
  effectiveChipState,
  parentVisualState,
  onCycle,
  expandedSlugs,
  onToggleExpanded,
}: OntologySectionProps) {
  const children = tree.children.get(node.node_id) ?? [];
  if (children.length === 0) return null;
  const leafChildren = children.filter(
    (c) => (tree.children.get(c.node_id) ?? []).length === 0,
  );
  const hasLeafChildren = leafChildren.length > 0;
  const expanded = expandedSlugs.has(node.node_id);
  return (
    <section>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-gray-500 uppercase">
          {node.label}
        </p>
        {hasLeafChildren && (
          <button
            type="button"
            onClick={() => onToggleExpanded(node.node_id)}
            title={expanded ? "Hide options" : `Show ${leafChildren.length} option${leafChildren.length === 1 ? "" : "s"}`}
            aria-label={expanded ? "Collapse" : "Expand"}
            aria-expanded={expanded}
            className="text-xs text-gray-500 hover:text-gray-700 px-1 cursor-pointer"
          >
            <span className="select-none">{expanded ? "▾" : "▸"}</span>
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {children.map((child) => (
          <OntologyChip
            key={child.node_id}
            node={child}
            tree={tree}
            brandCode={brandCode}
            depth={0}
            chipState={chipState}
            effectiveChipState={effectiveChipState}
            parentVisualState={parentVisualState}
            onCycle={onCycle}
            expandedSlugs={expandedSlugs}
            onToggleExpanded={onToggleExpanded}
          />
        ))}
      </div>
    </section>
  );
}

interface OntologyChipProps extends OntologySectionProps {
  depth: number;
}

function OntologyChip({
  node,
  tree,
  brandCode,
  depth,
  chipState,
  effectiveChipState,
  parentVisualState,
  onCycle,
  expandedSlugs,
  onToggleExpanded,
}: OntologyChipProps) {
  const children = tree.children.get(node.node_id) ?? [];
  const hasChildren = children.length > 0;
  const isLeaf = !hasChildren;

  // Hide leaves until the user expands their parent (per the user's
  // explicit request for a collapsible hierarchy display).
  const parentExpanded = node.parent_node_id
    ? expandedSlugs.has(node.parent_node_id)
    : false; // top-level leaves hide too — there's no "always-visible leaf"
  if (isLeaf && !parentExpanded) return null;

  // A parent shows an expand/collapse toggle only when it has at least
  // one leaf child (otherwise everything below it is hierarchy and
  // already visible).
  const leafChildren = children.filter(
    (c) => (tree.children.get(c.node_id) ?? []).length === 0,
  );
  const hasLeafChildren = leafChildren.length > 0;
  const expanded = expandedSlugs.has(node.node_id);

  const own = chipState(node.node_id);
  const visual = hasChildren
    ? parentVisualState(node.node_id)
    : effectiveChipState(node.node_id);
  const brandActive = node.active_brand_codes.includes(brandCode);

  // Glyph + colour for the visual state.
  const glyph =
    visual === "include" ? "✓" :
    visual === "exclude" ? "✕" :
    visual === "indeterminate" ? "–" : "";
  const tone =
    visual === "include" ? "text-emerald-700 bg-emerald-50 border-emerald-300" :
    visual === "exclude" ? "text-rose-700 bg-rose-50 border-rose-300 line-through" :
    visual === "indeterminate" ? "text-amber-700 bg-amber-50 border-amber-300" :
    "text-gray-700 bg-white border-gray-300";
  const inert = brandActive ? "" : "opacity-40 cursor-not-allowed";
  // The user's *explicit* state on a parent renders differently from
  // a passively-inherited descendant state (no underline / lighter bg).
  const inheritedHint =
    own === "off" && (visual === "include" || visual === "exclude")
      ? "italic"
      : "";
  // §4.6 — unmapped pseudo-nodes render with monospace label + faded
  // colour so operators see uncurated codes at a glance. Filterable.
  const unmappedStyle = node.is_unmapped ? "font-mono opacity-70" : "";

  return (
    <div>
      <div
        className={[
          "flex items-stretch gap-0 text-xs border rounded w-full",
          tone,
          inert,
          inheritedHint,
          unmappedStyle,
        ].join(" ")}
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          disabled={!brandActive}
          onClick={() => onCycle(node.node_id)}
          title={
            node.is_unmapped
              ? `Unmapped ${brandCode} code — click to filter; map it via seed/ontology.yml to give it a permanent home`
              : brandActive
                ? `${node.label} (click to cycle: include → exclude → off)`
                : `No ${brandCode} cars currently mapped to this option`
          }
          className="flex items-center gap-1 px-2 py-0.5 flex-1 text-left min-w-0 cursor-pointer disabled:cursor-not-allowed"
        >
          <span className="w-3 inline-block text-center font-semibold">
            {glyph}
          </span>
          <span className="flex-1 truncate">{node.label}</span>
        </button>
        {hasLeafChildren && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(node.node_id);
            }}
            title={expanded ? "Hide options" : `Show ${leafChildren.length} option${leafChildren.length === 1 ? "" : "s"}`}
            aria-label={expanded ? "Collapse" : "Expand"}
            aria-expanded={expanded}
            className="px-1.5 border-l border-current/20 hover:bg-black/5 cursor-pointer flex items-center justify-center"
          >
            <span className="inline-block w-3 text-center select-none">
              {expanded ? "▾" : "▸"}
            </span>
          </button>
        )}
      </div>
      {hasChildren && (
        <div className="mt-0.5 space-y-0.5">
          {children.map((child) => (
            <OntologyChip
              key={child.node_id}
              node={child}
              tree={tree}
              brandCode={brandCode}
              depth={depth + 1}
              chipState={chipState}
              effectiveChipState={effectiveChipState}
              parentVisualState={parentVisualState}
              onCycle={onCycle}
              expandedSlugs={expandedSlugs}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}
